import * as React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import UploadMusicVideo from '@components/upload/upload-music-video';
import Upload from '@components/upload';

import { VideoData } from '@webapp/types/common/video-data';
import { AssetUpload } from '@webapp/types/assets/asset-upload';
import AppState from '@webapp/store/state';
import { UploadError } from '@webapp/types/assets/upload-error';

import * as assetsSelectors from '@webapp/store/assets/selectors';
import { CreateAsset, CancelAssetUpload } from '../../../store/assets/action-creators';
import { getAssetTrackingId } from '../../../utils/assets/get-tracking-id';

interface UploadMusicVideoCtrlProps {
  className?: string;
  inline?: boolean;
  totalProgress: number;
  generalStatus: AssetUpload['status'];
  errors: UploadError[];
  error?: React.ReactNode;
  uploaded: number;
  total: number;
  createAsset: (file: File, trackingId: string) => CreateAsset;

  trackingId?: string;
  onUpload: (trackingId: string) => void;
  cancelUpload: (trackingId: string) => CancelAssetUpload;
}

const validateVideo = (video: VideoData) => {
  if (!video.type.startsWith('video/')) {
    throw new Error("Invalid Format");
  }
}

const mapStateToProps = (state: AppState, props: Pick<UploadMusicVideoCtrlProps, 'trackingId'>) => ({
  totalProgress: props.trackingId ? assetsSelectors.totalProgress(state.assets, props.trackingId) : 0,
  generalStatus: props.trackingId ? assetsSelectors.generalStatus(state.assets, props.trackingId) : undefined,
  errors: props.trackingId ? assetsSelectors.uploadErrors(state.assets, props.trackingId) : [],
  uploaded: props.trackingId ? assetsSelectors.getUploaded(state.assets, props.trackingId) : 0,
  total: props.trackingId ? assetsSelectors.getTotalUploads(state.assets, props.trackingId) : 0,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createAsset: (file: File, trackingId: string) => dispatch(new CreateAsset('video', file, undefined, trackingId)),
  cancelUpload: (trackingId: string) => dispatch(new CancelAssetUpload(trackingId))
})

export const UploadMusicVideoCtrl: React.FC<UploadMusicVideoCtrlProps> = (props) => {

  const propError = props.error;

  const [filename, setFilename] = React.useState<string>();
  const [error, setError] = React.useState<React.ReactNode>();

  React.useEffect(() => {
    setError(propError);
  }, [propError])

  const handleFile = async (file: File) => {
    const trackingId = getAssetTrackingId();
    setError(undefined);
    try {
      validateVideo({ type: file.type });
    } catch (e) {
      setError(e.message);
      return;
    }
    setFilename(file.name);
    props.createAsset(file, trackingId);
    props.onUpload(trackingId);
  };

  const handleFiles = async (files: FileList | File[]) => {
    setError(undefined);
    const promises = Array
      .from(files)
      .slice(0, 1)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((file) => handleFile(file));

    await Promise.all(promises);
  }

  return (
    <Upload
      className={props.className}
      status={props.generalStatus}
      progress={props.totalProgress}
      error={error}
      inline={props.inline}
      onChange={handleFiles}
    >
      <UploadMusicVideo
        accept="video/*"
        status={props.inline ? undefined : props.generalStatus}
        filename={filename}
        error={error}
        noIcon={props.inline}
        inline={props.inline}
        onChange={handleFiles}
        multiple={false}
        uploaded={props.uploaded}
        total={props.total}
        onCancel={() => {
          if (props.trackingId) {
            props.cancelUpload(props.trackingId);
          }
          setFilename(undefined);
        }}
      />
    </Upload>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(UploadMusicVideoCtrl);
